import React, { useEffect, useRef } from 'react';
import { Box, Paper, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import * as go from 'gojs';
import { CatalogStructure, ConfigurationTemplateDto } from './catalogStructure';
import { RequirementView } from './calculatorApi';

/** Was ein Knoten im Vorlagennetz darstellt. */
type TemplateNodeKind = 'template' | 'platform' | 'area' | 'requirement' | 'question';

/**
 * Das Netz einer einzelnen Vorlage.
 *
 * **Warum ein eigenes und nicht das große.** Das Gesamtnetz beantwortet „was gibt es und worauf
 * steht es". Hier ist die Frage enger und schärfer: was löst genau diese Vorlage aus? Gezeigt wird
 * deshalb nur, was sie berührt — die Unterbauten, die dafür nötig sind, die Bereiche, aus denen
 * gewählt wird, und die Fragen, die dabei offenbleiben.
 *
 * **Warum die Fragen mit im Bild sind.** Sie sind kein Nebenprodukt, sondern der halbe Inhalt einer
 * Vorlage. Ein Bild, das nur die gesetzten Positionen zeigt, ließe genau das weg, was den
 * Unterschied zwischen Hauswissen und Annahme ausmacht.
 */
export const TemplateDiagram: React.FC<{
    template: ConfigurationTemplateDto;
    structure: CatalogStructure;
    requirements: RequirementView[];
}> = ({ template, structure, requirements }) => {
    const theme = useTheme();
    const hostRef = useRef<HTMLDivElement>(null);
    const diagramRef = useRef<go.Diagram | null>(null);

    useEffect(() => {
        if (!hostRef.current) return undefined;

        const $ = go.GraphObject.make;
        const diagram = $(go.Diagram, hostRef.current, {
            allowMove: false,
            allowDelete: false,
            allowInsert: false,
            'undoManager.isEnabled': false,
            'toolManager.mouseWheelBehavior': go.WheelMode.Zoom,
            initialAutoScale: go.AutoScale.Uniform,
            layout: $(go.LayeredDigraphLayout, {
                direction: 90,
                layerSpacing: 42,
                columnSpacing: 26,
                setsPortSpots: false,
            }),
        });

        diagram.nodeTemplate = buildTemplate($, theme);
        diagram.linkTemplate = $(go.Link,
            {
                routing: go.Routing.AvoidsNodes,
                corner: 8,
                fromSpot: go.Spot.Bottom,
                toSpot: go.Spot.Top,
                toShortLength: 5,
            },
            $(go.Shape, { strokeWidth: 1.3, stroke: theme.palette.text.disabled },
                new go.Binding('strokeDashArray', 'open', (o: boolean) => (o ? [5, 4] : null))),
            $(go.Shape, { toArrow: 'Standard', strokeWidth: 0, scale: 0.7, fill: theme.palette.text.disabled }),
        );

        diagram.div!.style.backgroundColor = theme.palette.background.default;
        diagramRef.current = diagram;

        return () => {
            diagram.div = null;
            diagramRef.current = null;
        };
    }, [theme]);

    useEffect(() => {
        const diagram = diagramRef.current;
        if (!diagram) return;

        diagram.model = new go.GraphLinksModel({
            nodeDataArray: buildNodes(template, structure, requirements),
            linkDataArray: buildLinks(template, structure),
            linkKeyProperty: 'key',
            nodeCategoryProperty: 'kind',
        });
        diagram.addDiagramListener('InitialLayoutCompleted', () => diagram.zoomToFit());
    }, [template, structure, requirements]);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box ref={hostRef} sx={{ height: 420, width: '100%' }} />
        </Paper>
    );
};

function colorOf(kind: TemplateNodeKind, theme: Theme): string {
    switch (kind) {
        case 'template': return theme.palette.primary.main;
        case 'platform': return theme.palette.warning.main;
        case 'area': return theme.palette.info.main;
        case 'question': return theme.palette.info.main;
        default: return theme.palette.text.secondary;
    }
}

/** Eine Vorlage für alle Arten — hier ist die Menge klein genug, dass Unterschiede stören würden. */
function buildTemplate($: typeof go.GraphObject.make, theme: Theme): go.Node {
    const dark = theme.palette.mode === 'dark';

    return $(go.Node, 'Auto',
        {
            isShadowed: true,
            shadowBlur: dark ? 5 : 8,
            shadowColor: dark ? 'rgba(0,0,0,0.5)' : 'rgba(15,23,42,0.13)',
            shadowOffset: new go.Point(0, 1),
            fromSpot: go.Spot.Bottom,
            toSpot: go.Spot.Top,
            toolTip: $<go.Adornment>('ToolTip',
                $(go.TextBlock, { margin: 8, maxSize: new go.Size(300, NaN), font: '12px system-ui, sans-serif' },
                    new go.Binding('text', '', (d: { label: string; title: string }) =>
                        [d.label, d.title].filter(Boolean).join('\n')))),
        },
        $(go.Shape, 'RoundedRectangle',
            { parameter1: 6, strokeWidth: 1, fill: theme.palette.background.paper },
            new go.Binding('stroke', 'kind', (k: TemplateNodeKind) => colorOf(k, theme)),
            // Offene Punkte sind gestrichelt umrandet: sie stehen noch nicht, sie stehen zur Frage.
            new go.Binding('strokeDashArray', 'kind', (k: TemplateNodeKind) =>
                (k === 'question' ? [4, 3] : null)),
        ),
        $(go.Panel, 'Horizontal', { defaultAlignment: go.Spot.Top },
            $(go.Shape, 'Rectangle',
                { width: 3, stretch: go.GraphObject.Vertical, strokeWidth: 0 },
                new go.Binding('fill', 'kind', (k: TemplateNodeKind) => colorOf(k, theme))),
            $(go.Panel, 'Vertical',
                { defaultAlignment: go.Spot.Left, margin: new go.Margin(7, 10, 7, 9), width: 210 },
                $(go.TextBlock,
                    {
                        font: '600 11.5px ui-monospace, SFMono-Regular, Menlo, monospace',
                        alignment: go.Spot.Left,
                        maxSize: new go.Size(210, 15),
                        overflow: go.TextOverflow.Ellipsis,
                    },
                    new go.Binding('text', 'label'),
                    new go.Binding('stroke', 'kind', (k: TemplateNodeKind) => colorOf(k, theme))),
                $(go.TextBlock,
                    {
                        font: '11px system-ui, sans-serif',
                        stroke: theme.palette.text.secondary,
                        alignment: go.Spot.Left,
                        margin: new go.Margin(2, 0, 0, 0),
                        maxSize: new go.Size(210, 42),
                        overflow: go.TextOverflow.Ellipsis,
                    },
                    new go.Binding('text', 'title')),
            ),
        ),
    ) as unknown as go.Node;
}

function buildNodes(
    template: ConfigurationTemplateDto,
    structure: CatalogStructure,
    requirements: RequirementView[],
) {
    const byId = new Map(requirements.map((r) => [r.id, r]));
    const nodes: { key: string; kind: TemplateNodeKind; label: string; title: string }[] = [];

    nodes.push({
        key: 'tpl',
        kind: 'template',
        label: template.title,
        title: [template.anlagenart, template.isDraft ? 'Entwurf' : null].filter(Boolean).join(' · '),
    });

    // Nur die Unterbauten, die diese Vorlage wirklich braucht.
    const touched = new Set<string>();
    for (const o of template.openPositions ?? []) {
        const top = o.groupPath.split('.')[0];
        const group = structure.groups.find((g) => g.path === top);
        for (const p of group?.requiredPlatforms ?? []) touched.add(p);
    }

    for (const id of Array.from(touched)) {
        const p = structure.platforms.find((x) => x.id === id);
        if (p) nodes.push({ key: `plat:${id}`, kind: 'platform', label: p.title, title: p.fulfilledBy.join(' oder ') });
    }

    for (const pos of template.positions ?? []) {
        nodes.push({
            key: `req:${pos.requirementId}`,
            kind: 'requirement',
            label: `${pos.quantity}× ${pos.requirementId}`,
            title: byId.get(pos.requirementId)?.titel ?? '',
        });
    }

    (template.openPositions ?? []).forEach((o, i) => {
        nodes.push({
            key: `area:${o.groupPath}`,
            kind: 'area',
            label: `${o.quantity}× aus ${o.groupPath}`,
            title: 'Menge bekannt, Auswahl offen',
        });
        nodes.push({ key: `q:${i}`, kind: 'question', label: 'Rückfrage', title: o.question });
    });

    return nodes;
}

function buildLinks(template: ConfigurationTemplateDto, structure: CatalogStructure) {
    const links: { key: string; from: string; to: string; open?: boolean }[] = [];

    const touched = new Set<string>();
    for (const o of template.openPositions ?? []) {
        const top = o.groupPath.split('.')[0];
        const group = structure.groups.find((g) => g.path === top);
        for (const p of group?.requiredPlatforms ?? []) touched.add(p);
    }

    for (const id of Array.from(touched)) {
        links.push({ key: `l-tpl-${id}`, from: 'tpl', to: `plat:${id}`, open: touched.size > 1 });
    }

    for (const pos of template.positions ?? []) {
        links.push({ key: `l-tpl-${pos.requirementId}`, from: 'tpl', to: `req:${pos.requirementId}` });
    }

    (template.openPositions ?? []).forEach((o, i) => {
        const top = o.groupPath.split('.')[0];
        const group = structure.groups.find((g) => g.path === top);
        const parents = group?.requiredPlatforms ?? [];

        if (parents.length > 0) {
            for (const p of parents) {
                links.push({ key: `l-${p}-${o.groupPath}`, from: `plat:${p}`, to: `area:${o.groupPath}`, open: parents.length > 1 });
            }
        } else {
            links.push({ key: `l-tpl-${o.groupPath}`, from: 'tpl', to: `area:${o.groupPath}` });
        }

        links.push({ key: `l-q-${i}`, from: `area:${o.groupPath}`, to: `q:${i}`, open: true });
    });

    return links;
}

export default TemplateDiagram;
